// src/pages/auth/ResetPasswordPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import logonegro from '../../assets/images/logonegro.svg';
import fondoPantalla from '../../assets/images/fondo_pantalla.svg';
import '../../assets/styles/auth-common.css';
import Footer from '../../components/layout/footer';
import { useWindow } from '../../context/WindowContext';
import {PasswordValidation} from '../../types/auth.types';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { windowWidth } = useWindow();
  const [formData, setFormData] = useState({
    email: location.state?.email || '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState<'EN' | 'ES'>('EN');
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    hasLower: false,
    hasUpper: false,
    hasNumber: false,
    hasSpecial: false,
    isValid: false
  });

  useEffect(() => {
    if (formData.confirmPassword && formData.newPassword !== formData.confirmPassword) {
      setPasswordsMatch(false);
    } else {
      setPasswordsMatch(true);
    }

    const validation = {
      hasLower: /[a-z]/.test(formData.newPassword),
      hasUpper: /[A-Z]/.test(formData.newPassword),
      hasNumber: /[0-9]/.test(formData.newPassword),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword),
      isValid: false
    };

    validation.isValid = 
      validation.hasLower && 
      validation.hasUpper && 
      validation.hasNumber && 
      validation.hasSpecial;

    setPasswordValidation(validation);
  }, [formData.newPassword, formData.confirmPassword]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError(null);
  };

  const renderPasswordRequirements = () => (
    <div className="password-requirements">
      <h4>Password requirements:</h4>
      <ul>
        <li className={passwordValidation.hasLower ? 'valid' : 'invalid'}>
          {passwordValidation.hasLower ? '✓' : '○'} Lowercase letter
        </li>
        <li className={passwordValidation.hasUpper ? 'valid' : 'invalid'}>
          {passwordValidation.hasUpper ? '✓' : '○'} Uppercase letter
        </li>
        <li className={passwordValidation.hasNumber ? 'valid' : 'invalid'}>
          {passwordValidation.hasNumber ? '✓' : '○'} Number
        </li>
        <li className={passwordValidation.hasSpecial ? 'valid' : 'invalid'}>
          {passwordValidation.hasSpecial ? '✓' : '○'} Special character (!@#$%^&*)
        </li>
      </ul>
    </div>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.email || !formData.code || !formData.newPassword || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (!passwordValidation.isValid) {
      setError('Please ensure your password meets all requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      await authService.resetPassword({
        email: formData.email,
        code: formData.code,
        newPassword: formData.newPassword
      });
      navigate('/password-reset-success', { replace: true });
    } catch (err: any) {
      console.error('Reset password error:', err);
      setError(err.message || 'Error resetting password');
    } finally {
      setLoading(false);
    }
  };

  const rootStyle = {
    '--bg-image': `url(${fondoPantalla})`
  } as React.CSSProperties;

  return (
    <div style={{ backgroundColor: '#003DA5', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '30px', width: '100%' }}>
        {/* Logo centrado */}
        <div style={{ textAlign: 'center', width: '400px', maxWidth: '90vw' }}>
          <img src={logonegro} alt="Kanz Bank" style={{ width: 'auto', height: '252px', maxWidth: '100%' }} />
        </div>

        {/* Formulario centrado */}
        <form className="auth-form-container" onSubmit={handleSubmit} style={{ width: '400px', maxWidth: '90vw', position: 'relative', zIndex: 10, margin: '0', gridColumn: 'unset' }}>
          {error && (
            <div className="alert alert-danger">
              {error}
            </div>
          )}

          <div className="verification-info">
            <p>Enter the reset code sent to your email and choose a new password.</p>
            {formData.email && <strong>{formData.email}</strong>}
          </div>

          {!location.state?.email && (
            <div className="input-group">
              <label htmlFor="email">Email address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                disabled={loading || !!location.state?.email}
                required
              />
            </div>
          )}

          <div className="input-group">
            <label htmlFor="code">Reset Code</label>
            <input
              type="text"
              id="code"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              placeholder="Enter reset code"
              disabled={loading}
              required
            />
          </div>

          <div className="input-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="password-input">
              <input
                type={showNewPassword ? "text" : "password"}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter new password"
                disabled={loading}
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="password-toggle"
              >
                {showNewPassword ? 'Hide' : 'View'}
              </button>
            </div>
            {formData.newPassword && renderPasswordRequirements()}
          </div>

          <div className="input-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="password-input">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm new password"
                disabled={loading}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="password-toggle"
              >
                {showConfirmPassword ? 'Hide' : 'View'}
              </button>
            </div>
            {!passwordsMatch && formData.confirmPassword && (
              <span className="password-mismatch">Passwords do not match</span>
            )}
          </div>

          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading || !passwordsMatch || !passwordValidation.isValid}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>

          <div className="secondary-buttons">
            <button
              type="button"
              className="btn-signup"
              onClick={() => navigate('/signup')}
              disabled={loading}
            >
              Sign Up
            </button>
            <button 
              type="button"
              className="btn-validate"
              onClick={() => navigate('/')}
              disabled={loading}
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default ResetPasswordPage;